import React, { useState, useMemo } from 'react';
import { Clock, Pencil, X as XIcon, Power } from 'lucide-react';
import { Modal } from './Modal.js';
import { Button } from './Button.js';
import { CronPicker, describeCron, getNextRuns } from './CronPicker.js';
import { Badge } from './Badge.js';

interface ScheduleFieldProps {
  /** Current cron expression (e.g. "0 6 * * 1-5"). Empty string = no schedule. */
  value: string;
  onChange: (cron: string) => void;
  /** Optional enabled toggle — omit for simple cron-only fields */
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  /** Label shown above the compact display */
  label?: string;
  /** Compact mode hides the label, used when embedded in tables or status bars */
  compact?: boolean;
}

export const ScheduleField: React.FC<ScheduleFieldProps> = ({
  value,
  onChange,
  enabled,
  onEnabledChange,
  label = 'Schedule',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [draftEnabled, setDraftEnabled] = useState(enabled ?? true);

  const description = useMemo(() => (value ? describeCron(value) : 'No schedule'), [value]);
  const hasEnabled = enabled !== undefined && onEnabledChange !== undefined;

  const handleOpen = () => {
    setDraft(value);
    setDraftEnabled(enabled ?? true);
    setOpen(true);
  };

  const handleSave = () => {
    onChange(draft);
    if (hasEnabled) onEnabledChange(draftEnabled);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    if (hasEnabled) onEnabledChange(false);
    setOpen(false);
  };

  return (
    <>
      {/* Compact display */}
      {!compact && label && <label className="block text-sm text-muted mb-1">{label}</label>}
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-alt border rounded-sm text-sm text-body hover:border-strong transition group w-full"
      >
        <Clock size={14} className="text-faint shrink-0" />
        <span className="truncate flex-1 text-left">
          {value ? description : <span className="text-faint italic">No schedule set</span>}
        </span>
        {hasEnabled && value && (
          <Badge variant={enabled ? 'success' : 'neutral'} size="sm">
            {enabled ? 'On' : 'Off'}
          </Badge>
        )}
        <Pencil size={12} className="text-faint group-hover:text-body shrink-0 transition" />
      </button>

      {/* Schedule modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Configure Schedule"
        titleIcon={<Clock size={16} />}
        maxWidth="lg"
        footer={
          <>
            {value && (
              <Button variant="ghost" size="sm" onClick={handleClear}>
                <XIcon size={12} /> Clear
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <CronPicker value={draft || '* * * * *'} onChange={setDraft} />

          {hasEnabled && (
            <label className="flex items-center gap-2 cursor-pointer pt-2 border-t">
              <input
                type="checkbox"
                checked={draftEnabled}
                onChange={(e) => setDraftEnabled(e.target.checked)}
                className="rounded-sm"
              />
              <Power size={14} className={draftEnabled ? 'text-success' : 'text-faint'} />
              <span className="text-sm text-body">Schedule enabled</span>
            </label>
          )}

          <SchedulePreview cron={draft} />
        </div>
      </Modal>
    </>
  );
};

/** Compact inline display — read-only, for tables/status bars */
export const ScheduleDisplay: React.FC<{ cron: string; enabled?: boolean; className?: string }> = ({
  cron,
  enabled,
  className = '',
}) => {
  const description = useMemo(() => (cron ? describeCron(cron) : null), [cron]);
  if (!cron) return <span className={`text-faint text-xs ${className}`}>No schedule</span>;

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${className}`}>
      <Clock size={11} className="text-faint shrink-0" />
      <span className="text-body">{description}</span>
      {enabled !== undefined && (
        <Badge variant={enabled ? 'success' : 'neutral'} size="sm">
          {enabled ? 'On' : 'Off'}
        </Badge>
      )}
    </span>
  );
};

function SchedulePreview({ cron }: { cron: string }) {
  const nextRuns = useMemo(() => (cron ? getNextRuns(cron, 5) : []), [cron]);
  if (nextRuns.length === 0) return null;

  return (
    <div className="bg-surface-alt rounded-sm p-3">
      <span className="text-xs font-medium text-muted">Next runs (UTC)</span>
      <div className="mt-1.5 space-y-1">
        {nextRuns.map((d, i) => (
          <div key={i} className="text-xs text-body">
            {d.toLocaleString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
